/**
 * Item of the Day — Section JS
 * Handles:
 *  - Variant selection (updates hidden input, price, images, ATC button state)
 *  - Keyboard accessibility for variant buttons
 *  - Section-scoped so multiple instances on a page won't conflict
 */

(function () {
  'use strict';

  class ItemOfTheDay {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId;

      // DOM refs
      this.variantBtns = section.querySelectorAll('.iotd-variant-btn');
      this.variantIdInput = section.querySelector(`#IotdVariantId-${this.sectionId}`);
      this.priceWrap = section.querySelector(`#IotdPrice-${this.sectionId}`);
      this.atcBtn = section.querySelector(`#IotdAtcBtn-${this.sectionId}`);
      this.form = section.querySelector(`#IotdForm-${this.sectionId}`);
      this.img1 = section.querySelector(`#IotdImg1-${this.sectionId}`);
      this.img2 = section.querySelector(`#IotdImg2-${this.sectionId}`);

      // Labels (from section settings passed as data-attributes on section)
      this.atcLabel = section.dataset.atcLabel || 'Add to Cart';
      this.soldOutLabel = section.dataset.soldOutLabel || 'Sold Out';

      this._bindEvents();
    }

    _bindEvents() {
      if (this.form) {
        this.form.addEventListener('submit', (e) => this._onSubmit(e));
      }

      this.variantBtns.forEach((btn) => {
        // Click
        btn.addEventListener('click', () => this._onVariantSelect(btn));

        // Keyboard: Enter / Space
        btn.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this._onVariantSelect(btn);
          }
        });
      });
    }

    _onVariantSelect(btn) {
      if (btn.classList.contains('is-unavailable')) return;

      const optionIndex = parseInt(btn.dataset.optionIndex, 10);
      const variantId = btn.dataset.variantId;
      const price = parseInt(btn.dataset.price, 10);
      const comparePrice = parseInt(btn.dataset.comparePrice, 10);
      const available = btn.dataset.available === 'true';
      const imageSrc = btn.dataset.imageSrc;

      // Update selected state within the same option group
      const groupOptions = this.section.querySelectorAll(
        `.iotd-variant-btn[data-option-index="${optionIndex}"]`
      );
      groupOptions.forEach((b) => {
        b.classList.remove('is-selected');
        b.setAttribute('aria-checked', 'false');
      });
      btn.classList.add('is-selected');
      btn.setAttribute('aria-checked', 'true');

      // Update hidden variant ID input
      if (this.variantIdInput && variantId) {
        this.variantIdInput.value = variantId;
      }

      // Update price display
      if (this.priceWrap && !isNaN(price)) {
        this._updatePrice(price, comparePrice);
      }

      // Update ATC button
      if (this.atcBtn) {
        if (available && variantId) {
          this.atcBtn.disabled = false;
          this.atcBtn.textContent = this.atcLabel;
        } else {
          this.atcBtn.disabled = true;
          this.atcBtn.textContent = this.soldOutLabel;
        }
      }

      // Update first image if variant has its own image
      if (imageSrc && imageSrc !== 'null' && imageSrc !== '' && this.img1) {
        this._swapImage(this.img1, imageSrc);
      }
    }

    _updatePrice(price, comparePrice) {
      const moneyFormatter = (cents) => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: Shopify?.currency?.active || 'USD',
          minimumFractionDigits: 2,
        }).format(cents / 100);
      };

      const formatted = moneyFormatter(price);
      const formattedCompare = !isNaN(comparePrice) && comparePrice > price
        ? moneyFormatter(comparePrice)
        : null;

      if (formattedCompare) {
        this.priceWrap.innerHTML = `
          <span class="iotd-info__price iotd-info__price--sale">${formatted}</span>
          <span class="iotd-info__price iotd-info__price--compare">${formattedCompare}</span>
        `;
      } else {
        this.priceWrap.innerHTML = `<span class="iotd-info__price">${formatted}</span>`;
      }
    }

    async _onSubmit(e) {
      e.preventDefault();
      
      if (!this.atcBtn || this.atcBtn.disabled) return;
      
      const variantId = this.variantIdInput ? this.variantIdInput.value : null;
      if (!variantId) return;

      const originalText = this.atcBtn.textContent;
      this.atcBtn.disabled = true;
      this.atcBtn.textContent = 'Adding...';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: parseInt(variantId),
            quantity: 1,
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname
          })
        });

        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.description || 'Could not add to cart');
        }

        const parsedState = await res.json();

        // Update cart count bubble
        const bubbleHtml = parsedState.sections?.['cart-icon-bubble'];
        if (bubbleHtml) {
          const tempBubble = document.createElement('div');
          tempBubble.innerHTML = bubbleHtml;
          const newBubble = tempBubble.querySelector('#cart-icon-bubble');
          const currentBubble = document.getElementById('cart-icon-bubble');
          if (newBubble && currentBubble) {
            currentBubble.innerHTML = newBubble.innerHTML;
          }
        }

        // Open cart drawer or notification
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          if (typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(parsedState);
          } else {
            // Manual fallback for themes without renderContents
            const drawerHtml = parsedState.sections?.['cart-drawer'];
            if (drawerHtml) {
              const tempDrawer = document.createElement('div');
              tempDrawer.innerHTML = drawerHtml;
              const newDrawer = tempDrawer.querySelector('cart-drawer') || tempDrawer.firstElementChild;
              if (newDrawer) {
                cartDrawer.innerHTML = newDrawer.innerHTML;
              }
            }

            // Force open classes
            cartDrawer.classList.add('active', 'animate', 'is-open');
            cartDrawer.classList.remove('is-empty');
            
            const innerDrawer = cartDrawer.querySelector('.drawer');
            if (innerDrawer) {
              innerDrawer.classList.add('active', 'animate', 'is-open');
            }
            document.body.classList.add('overflow-hidden');
          }
        } else {
          const cartNotification = document.querySelector('cart-notification');
          if (cartNotification && typeof cartNotification.renderContents === 'function') {
            cartNotification.renderContents(parsedState);
          } else if (cartNotification && typeof cartNotification.open === 'function') {
            cartNotification.open();
          } else {
            const cartIcon = document.getElementById('cart-icon-bubble');
            if (cartIcon) {
              cartIcon.click();
            } else {
              window.location.href = window.routes?.cart_url || '/cart';
            }
          }
        }

        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: parsedState }));

        this.atcBtn.textContent = 'Added ✓';
        this.atcBtn.classList.add('is-added');

        setTimeout(() => {
          this.atcBtn.textContent = originalText;
          this.atcBtn.disabled = false;
          this.atcBtn.classList.remove('is-added');
        }, 2000);

      } catch (err) {
        console.error('[ItemOfTheDay] Add to cart error:', err);
        this.atcBtn.textContent = 'Error — try again';
        setTimeout(() => {
          this.atcBtn.textContent = originalText;
          this.atcBtn.disabled = false;
        }, 2500);
      }
    }

    _swapImage(imgEl, newSrc) {
      if (!imgEl || !newSrc) return;

      imgEl.style.opacity = '0';
      imgEl.style.transition = 'opacity 0.3s ease';

      const tmp = new Image();
      tmp.onload = () => {
        imgEl.src = newSrc;
        imgEl.style.opacity = '1';
      };
      tmp.onerror = () => {
        imgEl.style.opacity = '1';
      };
      tmp.src = newSrc;
    }
  }

  // ── Init on DOM ready ──
  function initSections() {
    document.querySelectorAll('[id^="ItemOfTheDay-"]').forEach((section) => {
      if (!section._iotdInit) {
        section._iotdInit = true;
        new ItemOfTheDay(section);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSections);
  } else {
    initSections();
  }

  // ── Shopify Theme Editor live-reload support ──
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('[id^="ItemOfTheDay-"]');
    if (section && !section._iotdInit) {
      section._iotdInit = true;
      new ItemOfTheDay(section);
    }
  });
})();
